import { ShieldCheckIcon, RotateCwIcon } from "../../icons";
import { Card, CardHeader, CardTitle, CardContent } from "../../card";
import { Button } from "../../button";
import { IntentBadge } from "../status-badge";
import { cn } from "../../utils";
import {
  type AuditFindingVm,
  type AuditFindingSeverity,
  type AuditFindingStatus,
  scoreIntent,
  freshnessLabel,
  useRunAudit,
  FindingRow,
} from "./section-audit-shared";

export type { AuditFindingVm, AuditFindingSeverity, AuditFindingStatus };

export interface SectionAuditPanelProps {
  sectionLabel: string;
  /** One-line description of what this audit checks. */
  focus: string;
  /** Latest agent score 0-100, or null if never run / last run had none. */
  score: number | null;
  /** Days since last successful audit; null = never run. */
  ageDays: number | null;
  cadenceDays: number;
  /** A re-audit is due (never run or older than cadence). */
  isDue: boolean;
  /** An audit is currently running for this section. */
  running: boolean;
  /** Status of the most recent run (for surfacing errors). */
  lastRunStatus: "running" | "completed" | "error" | "timeout" | "superseded" | null;
  findings: AuditFindingVm[];
  onRunAudit: () => Promise<void>;
  onFix: (findingId: number) => Promise<void>;
  onDismiss: (findingId: number) => Promise<void>;
}

/**
 * Universal "best-in-class" audit panel for a core function (section). Shows
 * the latest audit score + freshness, a one-click "Run audit" that spawns a
 * coding agent, and the open findings — each with a one-click "Fix" that
 * spawns an agent to resolve it. Additive surface; appends below the section
 * dashboard. The plain-Card sibling of `SectionAuditCard`; both share their
 * data model + finding-row logic via `section-audit-shared`.
 */
export function SectionAuditPanel({
  sectionLabel,
  focus,
  score,
  ageDays,
  cadenceDays,
  isDue,
  running,
  lastRunStatus,
  findings,
  onRunAudit,
  onFix,
  onDismiss,
}: SectionAuditPanelProps) {
  const { busy, error, runAudit } = useRunAudit(onRunAudit, running);
  const open = findings.filter(
    (f) => f.status === "open" || f.status === "fixing",
  );

  return (
    <Card data-section-audit-panel>
      <CardHeader className="px-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-0.5 min-w-0">
            <CardTitle className="flex items-center gap-2 text-foreground">
              <ShieldCheckIcon aria-hidden="true" className="size-4 shrink-0" />
              Best-in-class audit
              {score != null ? (
                <IntentBadge intent={scoreIntent(score)} className="tabular-nums">
                  {score}/100
                </IntentBadge>
              ) : null}
            </CardTitle>
            <p className="type-body text-muted-foreground truncate">{focus}</p>
          </div>
          <Button
            type="button"
            variant={isDue ? "default" : "outline"}
            size="sm"
            onClick={runAudit}
            disabled={busy}
            aria-label={`Run the ${sectionLabel} best-in-class audit`}
          >
            <RotateCwIcon
              aria-hidden="true"
              className={cn(busy && "animate-spin")}
            />
            {busy
              ? "Auditing…"
              : ageDays == null
                ? "Run audit"
                : "Re-audit"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 px-0 pb-0">
        <p className="px-4 type-body text-muted-foreground">
          {freshnessLabel(ageDays, cadenceDays, isDue)}
        </p>

        {error ? (
          <p className="px-4 type-body text-destructive">{error}</p>
        ) : null}

        {lastRunStatus === "error" || lastRunStatus === "timeout" ? (
          <p className="px-4 type-body text-warning-fg">
            The last audit run {lastRunStatus === "timeout" ? "timed out" : "failed"}.
            Try running it again.
          </p>
        ) : null}

        {open.length === 0 ? (
          <p className="px-4 pb-4 type-body text-muted-foreground">
            {ageDays == null
              ? `Run the first audit to check whether ${sectionLabel} is best-in-class.`
              : `No open findings — ${sectionLabel} looks best-in-class.`}
          </p>
        ) : (
          <ul aria-label={`${sectionLabel} audit findings`}>
            {open.map((f, idx) => (
              <li
                key={f.id}
                className={cn(
                  "px-4 py-3 border-b border-border/40",
                  idx === 0 && "pt-0",
                  idx === open.length - 1 && "border-b-0 pb-0",
                )}
              >
                <FindingRow
                  finding={f}
                  onFix={onFix}
                  onDismiss={onDismiss}
                />
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
